import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import userService from "@/services/userService";
import profileService from "@/services/profileService";

// ROLE_GUEST é o perfil do token anônimo do cliente; não é atribuído a usuários
const HIDDEN_PROFILES = ["ROLE_GUEST"];

// Valor do Select para "sem empresa" (o Select não aceita valor vazio)
const NO_COMPANY = "__none__";

const EMPTY_FORM = {
  username: "",
  email: "",
  password: "",
  enabled: true,
  roles: ["ROLE_USER"],
  company_code: NO_COMPANY,
};

const sameRoles = (a, b) =>
  a.length === b.length && a.every((role) => b.includes(role));

/**
 * Modal para criar ou editar usuário
 */
export default function UserFormModal({
  open,
  onOpenChange,
  user,
  onSave,
  loading,
  isOwner = false,
  profiles = [],
  menus = [],
  loadingProfiles = false,
  companies = [],
  defaultCompanyCode = null,
  // Só o ROLE_OWNER escolhe a empresa; para os demais a API grava na
  // empresa de quem está logado e ignora company_code
  canChooseCompany = false,
}) {
  // Só ROLE_ADMIN/ROLE_OWNER abrem este modal e podem usar qualquer empresa
  const isEditing = !!user;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [originalRoles, setOriginalRoles] = useState([]);
  const [originalCompany, setOriginalCompany] = useState(NO_COMPANY);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      const roles = (user.roles || []).map((r) => userService.normalizeRole(r));
      setFormData({
        username: user.username || "",
        email: user.email || "",
        password: "", // Não mostrar senha ao editar
        enabled: user.enabled !== false,
        roles,
        company_code: user.company_code || NO_COMPANY,
      });
      setOriginalRoles(roles);
      setOriginalCompany(user.company_code || NO_COMPANY);
    } else {
      setFormData({
        ...EMPTY_FORM,
        company_code: defaultCompanyCode || NO_COMPANY,
      });
      setOriginalRoles([]);
      setOriginalCompany(NO_COMPANY);
    }
    setErrors({});
    setShowPassword(false);
  }, [user, open, defaultCompanyCode]);

  // Empresas selecionáveis + a atual do usuário (caso não esteja na lista)
  const companyOptions = companies
    .filter((c) => c?.code)
    .map((c) => ({ value: c.code, label: c.name || c.code }));
  if (
    user?.company_code &&
    !companyOptions.some((option) => option.value === user.company_code)
  ) {
    companyOptions.push({
      value: user.company_code,
      label: user.company_name || user.company_code,
    });
  }

  // Perfis vindos de /profiles; ROLE_OWNER só pode ser concedido por owner
  const menuLabels = menus.reduce((acc, menu) => {
    acc[menu.link] = menu.label;
    return acc;
  }, {});

  const roleOptions = profiles
    .map((profile) => {
      const value = profileService.normalizeName(profile.name);
      const blocked =
        HIDDEN_PROFILES.includes(value) || (value === "ROLE_OWNER" && !isOwner);
      const profileMenus = (profile.menus || []).map(
        (link) => menuLabels[link] || link,
      );
      return {
        value,
        label: profileService.getProfileLabel(value),
        description:
          profileMenus.length > 0
            ? `Menus: ${profileMenus.join(", ")}`
            : "Nenhum menu vinculado",
        blocked,
      };
    })
    // Perfis bloqueados só aparecem se o usuário já os possui (mantidos)
    .filter((option) => !option.blocked || originalRoles.includes(option.value))
    .map((option) => ({ ...option, locked: option.blocked }));

  // Roles do usuário que não existem mais em /profiles
  originalRoles
    .filter((role) => !roleOptions.some((option) => option.value === role))
    .forEach((role) =>
      roleOptions.push({
        value: role,
        label: profileService.getProfileLabel(role),
        locked: true,
      }),
    );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleToggleRole = (role, checked) => {
    const roles = checked
      ? [...formData.roles, role]
      : formData.roles.filter((r) => r !== role);
    handleChange("roles", roles);
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.username.length > 255) {
      newErrors.username = "Nome deve ter no máximo 255 caracteres";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!userService.isValidEmail(formData.email.trim())) {
      newErrors.email = "Email inválido";
    }

    // Senha obrigatória só na criação; na edição, valida se informada
    if (!isEditing && !formData.password) {
      newErrors.password = "Senha é obrigatória";
    } else if (
      formData.password &&
      (formData.password.length < 6 || formData.password.length > 100)
    ) {
      newErrors.password = "Senha deve ter entre 6 e 100 caracteres";
    }

    if (formData.roles.length === 0) {
      newErrors.roles = "Selecione ao menos um perfil";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const userData = {
      email: formData.email.trim(),
      username: formData.username.trim(),
      enabled: formData.enabled,
    };

    // Na edição, senha em branco mantém a atual
    if (formData.password) {
      userData.password = formData.password;
    }

    // Na edição, roles omitidas mantêm as atuais
    if (!isEditing || !sameRoles(formData.roles, originalRoles)) {
      userData.roles = formData.roles;
    }

    // Empresa: na criação, omitida = empresa de quem cadastra (ou nenhuma);
    // na edição, omitida mantém a atual e "" remove o vínculo.
    // Só o owner envia company_code.
    if (canChooseCompany && !isEditing) {
      if (formData.company_code !== NO_COMPANY) {
        userData.company_code = formData.company_code;
      }
    } else if (canChooseCompany && formData.company_code !== originalCompany) {
      userData.company_code =
        formData.company_code === NO_COMPANY ? "" : formData.company_code;
    }

    onSave(userData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Usuário" : "Novo Usuário"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize as informações do usuário. Deixe a senha em branco para não alterar."
              : "Preencha os dados para criar um novo usuário."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="username">Nome</Label>
            <Input
              id="username"
              placeholder="Nome do usuário"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
              className={errors.username ? "border-red-500" : ""}
              disabled={loading}
            />
            {errors.username && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.username}</span>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="usuario@exemplo.com"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className={errors.email ? "border-red-500" : ""}
              disabled={loading}
            />
            {errors.email && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          {/* Senha */}
          <div className="space-y-2">
            <Label htmlFor="password">
              Senha {!isEditing && <span className="text-red-500">*</span>}
              {isEditing && (
                <span className="text-gray-500 text-xs ml-2">
                  (deixe em branco para não alterar)
                </span>
              )}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={
                  isEditing ? "Nova senha (opcional)" : "Mínimo 6 caracteres"
                }
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className={errors.password ? "border-red-500 pr-10" : "pr-10"}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.password}</span>
              </div>
            )}
          </div>

          {/* Empresa (só o owner escolhe) */}
          {canChooseCompany ? (
          <div className="space-y-2">
            <Label htmlFor="company_code">Empresa</Label>
            <Select
              value={formData.company_code}
              onValueChange={(value) => handleChange("company_code", value)}
              disabled={loading}
            >
              <SelectTrigger
                id="company_code"
                className={errors.company_code ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Selecione a empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_COMPANY}>
                  Sem empresa vinculada
                </SelectItem>
                {companyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.company_code ? (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.company_code}</span>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Empresa à qual o usuário terá acesso. Perfis que não são
                proprietário só enxergam dados da própria empresa.
              </p>
            )}
          </div>
          ) : (
            <p className="text-xs text-gray-500">
              O usuário será vinculado à sua empresa.
            </p>
          )}

          {/* Funções */}
          <div className="space-y-2">
            <Label>
              Perfis de acesso <span className="text-red-500">*</span>
            </Label>
            <div
              className={`space-y-2 rounded-md border p-3 ${
                errors.roles ? "border-red-500" : "border-gray-200"
              }`}
            >
              {loadingProfiles && (
                <p className="text-sm text-gray-500">Carregando perfis...</p>
              )}
              {!loadingProfiles && roleOptions.length === 0 && (
                <p className="text-sm text-gray-500">
                  Nenhum perfil cadastrado. Crie um perfil na aba "Perfis de
                  Acesso".
                </p>
              )}
              {roleOptions.map((role) => (
                <label
                  key={role.value}
                  htmlFor={`role-${role.value}`}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <Checkbox
                    id={`role-${role.value}`}
                    checked={formData.roles.includes(role.value)}
                    onCheckedChange={(checked) =>
                      handleToggleRole(role.value, checked === true)
                    }
                    disabled={loading || role.locked}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">
                      {role.label}
                    </span>
                    {role.description && (
                      <span className="block text-xs text-gray-500">
                        {role.description}
                      </span>
                    )}
                    {role.locked && (
                      <span className="block text-xs text-gray-500">
                        Mantida — não pode ser alterada por você
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {errors.roles && (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.roles}</span>
              </div>
            )}
          </div>

          {/* Ativo */}
          <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
            <div>
              <Label htmlFor="enabled">Usuário ativo</Label>
              <p className="text-xs text-gray-500">
                Usuários inativos não conseguem acessar o sistema
              </p>
            </div>
            <Switch
              id="enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) => handleChange("enabled", checked)}
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#1b5e20" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#144919")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#1b5e20")
              }
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
